import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import debounce from 'lodash.debounce';

import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

const Lightbox = dynamic(() => import('yet-another-react-lightbox'), { ssr: false });
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/styles.css';
import './gallery.css';

import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { buildImageUrl } from '../../utils/utils';
import ExitFullscreenIcon from '../Icon/ExitFullscreenIcon';
import FullscreenIcon from '../Icon/FullscreenIcon';
import GridIcon from '../Icon/GridIcon';
import PauseIcon from '../Icon/PauseIcon';
import PlayIcon from '../Icon/PlayIcon';
import { Close, ZoomIn, ZoomOut } from '@mui/icons-material';

type Slide = {
  fullSrc: string;
  previewSrc: string;
  thumbnail: string;
  alt: string;
};

export const GalleryView = ({ t, images, projectId, propertyId }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isProperty = !!propertyId;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [slides, setSlides] = useState<Slide[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSlides = async () => {
      setIsLoading(true);
      if (!images || !Array.isArray(images)) {
        setSlides([]);
        setIsLoading(false);
        return;
      }

      const previewWidth = isTablet ? 400 : 600;
      const previewHeight = Math.floor((previewWidth * 3) / 4);

      const results = await Promise.all(
        images.map(async (img) => {
          const isStaging = img.gallery_Images.startsWith('staging/');
          const pathInfo = {
            galleryPath: isProperty ? 'property_gallery' : 'project_gallery',
            pathId: isProperty ? propertyId : projectId,
            fileName: isStaging ? undefined : img.gallery_Images,
            fullPath: isStaging ? img.gallery_Images : undefined,
          };

          const fullSrc = buildImageUrl({ ...pathInfo, isDesktop });
          const previewSrc = buildImageUrl({
            ...pathInfo,
            width: previewWidth,
            height: previewHeight,
            quality: 40,
          });
          const thumbnail = buildImageUrl({
            ...pathInfo,
            width: 200,
            height: 150,
            quality: 20,
          });

          return { fullSrc, previewSrc, thumbnail, alt: img.alt || '' };
        })
      );

      setSlides(results);
      setIsLoading(false);
    };

    loadSlides();
  }, [images, isDesktop, isProperty, projectId, propertyId, isMobile, isTablet]);

  const [index, setIndex] = useState(-1);
  const [maxPreview, setMaxPreview] = useState(5);

  const previewSlides = useMemo(() => slides.slice(0, maxPreview), [slides, maxPreview]);
  const remaining = slides.length - maxPreview;

  useEffect(() => {
    const handleResize = debounce(() => {
      setMaxPreview(window.innerWidth < 640 ? 3 : 5);
    }, 200);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel?.();
    };
  }, []);

  const iconSize = isMobile ? 20 : isTablet ? 20 : 24;
  const iconSizeMui = isMobile ? 29 : isTablet ? 32 : 36;

  const gridClass = useMemo(() => {
    const n = previewSlides.length;
    if (isMobile) {
      return (
        'gallery-grid ' +
        (n === 1 ? 'gallery-grid--mobile-1' : n === 2 ? 'gallery-grid--mobile-2' : n === 3 ? 'gallery-grid--mobile-3' : '')
      );
    }
    return (
      'gallery-grid ' +
      (n === 1
        ? 'gallery-grid--1'
        : n === 2
        ? 'gallery-grid--2'
        : n === 3
        ? 'gallery-grid--3'
        : n === 4
        ? 'gallery-grid--4'
        : n === 5
        ? 'gallery-grid--5'
        : '')
    );
  }, [isMobile, previewSlides.length]);

  const plugins = useMemo(() => (index < 0 ? [] : [Counter, Fullscreen, Slideshow, Thumbnails, Zoom]), [index]);

  return (
    <Box>
      {/* Grid Preview */}
      <Box className={gridClass}>
        {(isLoading ? Array(maxPreview).fill(null) : previewSlides).map((slide, i) => {
          const isLast = !isLoading && i === previewSlides.length - 1 && remaining > 0;
          return (
            <Box
              key={i}
              className="gallery-item"
              sx={{
                fontSize: isMobile ? '1rem' : '1.2rem',
                position: 'relative',
                aspectRatio: '4/3',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}
              onClick={!isLoading ? () => setIndex(i) : undefined}
            >
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ bgcolor: '#eee' }} />
              ) : (
                <>
                  <Image
                    src={slide.previewSrc}
                    alt={slide.alt || `Picture ${i + 1}`}
                    fill
                    priority={i === 0}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    sizes={isMobile ? '100vw' : '50vw'}
                    style={{ objectFit: 'cover', display: 'block' }}
                  />
                  {isLast && <Box className="gallery-overlay">+ {remaining} {t('picture')}</Box>}
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Full Lightbox */}
      <Lightbox
        index={index}
        open={index >= 0}
        close={() => setIndex(-1)}
        slides={slides.map((s) => ({ src: s.fullSrc, thumbnail: s.thumbnail }))}
        plugins={plugins}
        counter={{
          container: {
            style: {
              top: isMobile ? 8 : 6,
              left: isMobile ? 40 : 60,
              fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px',
              alignItems: 'center',
            },
          },
        }}
        thumbnails={{
          position: 'bottom',
          showToggle: true,
          gap: isMobile ? 2 : 5,
          imageFit: 'cover',
          padding: 0,
          borderRadius: 8,
          borderColor: 'black',
          hidden: false,
          vignette: false,
        }}
        toolbar={{ buttons: ['thumbnails', 'zoom', 'slideshow', 'fullscreen', 'close'] }}
        carousel={{ finite: false, preload: 2 }}
        animation={{ fade: 300, swipe: 500 }}
        render={{
          iconThumbnailsVisible: () => <GridIcon width={iconSize} height={iconSize} />,
          iconThumbnailsHidden: () => <GridIcon width={iconSize} height={iconSize} />,
          iconZoomIn: () => <ZoomIn sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconZoomOut: () => <ZoomOut sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconSlideshowPlay: () => <PlayIcon width={iconSize} height={iconSize} />,
          iconSlideshowPause: () => <PauseIcon width={iconSize} height={iconSize} />,
          iconEnterFullscreen: () => <FullscreenIcon width={iconSize} height={iconSize} />,
          iconExitFullscreen: () => <ExitFullscreenIcon width={iconSize} height={iconSize} />,
          iconClose: () => <Close sx={{ width: iconSizeMui, height: iconSizeMui }} />,
        }}
        styles={{
          toolbar: { padding: '16px 12px', gap: isMobile ? '0px' : isTablet ? '6px' : '10px' },
          icon: { width: isTablet ? 40 : 62, height: isTablet ? 40 : 62 },
        }}
      />
    </Box>
  );
};
